import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  EventRefundRequestDto,
  ApproveEventRefundRequestDto,
  ResponseDto,
} from "@mimber/schema"
import { eventRefundsKeys } from "../events.utils"

export type ApproveRefundRequestVariables = {
  refundUuid: string
  data: ApproveEventRefundRequestDto
}

export const useApproveRefundRequest = (
  eventUuid: string,
  options?: UseMutationOptions<
    ResponseDto<EventRefundRequestDto>,
    Error,
    ApproveRefundRequestVariables
  >
): UseMutationResult<
  ResponseDto<EventRefundRequestDto>,
  Error,
  ApproveRefundRequestVariables
> => {
  guardAppUsage("mimber-organization", "useApproveRefundRequest")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ refundUuid, data }: ApproveRefundRequestVariables) => {
      const response = await server.post<ResponseDto<EventRefundRequestDto>>(
        `events/${eventUuid}/refunds/${refundUuid}/approve`,
        data
      )
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: eventRefundsKeys.findAll(eventUuid),
      })
      queryClient.invalidateQueries({
        queryKey: eventRefundsKeys.findOne(eventUuid, variables.refundUuid),
      })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
